import React, { useState, useEffect } from 'react';
import { fetchContestMCQs, submitContestResult } from '../api/contestApi';
import LatexText from "../components/LatexText";
import { useAuthUser } from './useAuthUser';

const ProblemCards = ({ contestId, timeLeft }) =>
{
    const { userAuth } = useAuthUser();

    const [mcqs, setMcqs] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [loadingQ, setLoadingQ] = useState(true);
    const [userAnswers, setUserAnswers] = useState({});
    const [contestSubmitted, setContestSubmitted] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(timeLeft);

    const [visitedQuestions, setVisitedQuestions] = useState(new Set());
    const [markedForReview, setMarkedForReview] = useState(new Set());

    const OPTION_ORDER = ['A', 'B', 'C', 'D'];
    const selectedAnswer = userAnswers[currentQuestion] || '';

    /* ---------------- LOAD MCQs ---------------- */

    useEffect(() =>
    {
        const loadMCQs = async () =>
        {
            setLoadingQ(true);
            const questions = await fetchContestMCQs(contestId, userAuth);
            setMcqs(questions);
            setLoadingQ(false);
        };

        if (contestId && userAuth) loadMCQs();
    }, [contestId, userAuth]);

    useEffect(() =>
    {
        setVisitedQuestions(prev =>
        {
            const s = new Set(prev);
            s.add(currentQuestion);
            return s;
        });
    }, [currentQuestion]);

    /* ---------------- TIMER ---------------- */

    useEffect(() =>
    {
        if (timeRemaining > 0 && !contestSubmitted)
        {
            const timer = setTimeout(() =>
            {
                setTimeRemaining(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }

        if (timeRemaining === 0 && !contestSubmitted)
        {
            handleSubmitContest();
        }
    }, [timeRemaining, contestSubmitted]);

    /* ---------------- HANDLERS ---------------- */

    const handleAnswerSelect = (answer) =>
    {
        setUserAnswers(prev => ({ ...prev, [currentQuestion]: answer }));
    };

    const handleSubmitContest = async () =>
    {
        if (!userAuth || contestSubmitted) return;

        try
        {
            setContestSubmitted(true);
            const answers = {};
            mcqs.forEach((mcq, index) =>
            {
                answers[mcq.id] = userAnswers[index] || null;
            });

            await submitContestResult({
                contestId,
                userName: userAuth.displayName,
                answers
            });
        }
        catch (err)
        {
            console.error(err);
            alert("Error submitting contest");
            setContestSubmitted(false);
        }
    };

    const formatTime = (s) =>
    {
        const m = Math.floor(s / 60);
        const r = s % 60;
        return `${m}:${r.toString().padStart(2, '0')}`;
    };

    const toggleMarkForReview = () =>
    {
        setMarkedForReview(prev =>
        {
            const s = new Set(prev);
            s.has(currentQuestion) ? s.delete(currentQuestion) : s.add(currentQuestion);
            return s;
        });
    };

    const getPaletteColor = (index) =>
    {
        if (markedForReview.has(index)) return 'bg-purple-500 text-white';
        if (userAnswers[index]) return 'bg-green-500 text-white';
        if (visitedQuestions.has(index)) return 'bg-red-500 text-white';
        return 'bg-white text-black border';
    };

    /* ---------------- STATES ---------------- */

    if (loadingQ)
    {
        return <div className="p-6 text-center text-xl">Loading questions...</div>;
    }

    if (contestSubmitted)
    {
        return (
            <div className="p-6 text-center">
                <h2 className="text-3xl font-bold text-green-600 mb-4">
                    Contest Submitted!
                </h2>
                <p className="text-xl">
                    Thank you for participating.
                </p>
            </div>
        );
    }

    if (mcqs.length === 0)
    {
        return (
            <div className="p-6 text-center text-xl text-red-600">
                Unable to load contest questions.
            </div>
        );
    }

    const currentMCQ = mcqs[currentQuestion];

    return (
        <div className="max-w-full mx-auto p-3 sm:p-6 flex flex-col sm:flex-row gap-6">

            {/* ================= QUESTION AREA ================= */}
            <div className="w-full sm:w-3/4">
                <div className="mb-4 flex justify-between items-center">
                    <div>
                        <h2 className="text-xl sm:text-2xl mb-1">
                            Contest {contestId}
                        </h2>
                        <div className="text-sm sm:text-xl">
                            Question {currentQuestion + 1} of {mcqs.length}
                        </div>
                    </div>

                    <div className="text-right">
                        <div className="text-xl sm:text-2xl font-bold text-red-600">
                            {formatTime(timeRemaining)}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">
                            Time Remaining
                        </div>
                    </div>
                </div>

                {/* PROGRESS */}
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${((currentQuestion + 1) / mcqs.length) * 100}%` }}
                    />
                </div>

                {/* QUESTION CARD */}
                <div className="bg-white border border-black rounded-lg shadow-xl p-4 sm:p-6">
                    {currentMCQ.Question_Image && (
                        <img
                            src={currentMCQ.Question_Image}
                            alt=""
                            className="max-h-[300px] mx-auto mb-4 rounded-lg border"
                        />
                    )}

                    <h3 className="text-lg sm:text-2xl font-semibold mb-4 break-words">
                        <LatexText text={currentMCQ.question} />
                    </h3>

                    <div className="space-y-3 mb-6">
                        {OPTION_ORDER.map(key => (
                            <button
                                key={key}
                                onClick={() => handleAnswerSelect(key)}
                                className={`w-full text-left p-3 sm:p-4 border rounded-lg ${
                                    selectedAnswer === key
                                        ? 'bg-blue-100 border-blue-500'
                                        : 'border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                <span className="font-semibold text-blue-600">
                                    {key}.
                                </span>{' '}
                                <LatexText text={currentMCQ.options[key]} />
                            </button>
                        ))}
                    </div>

                    {/* CONTROLS */}
                    <div className="flex flex-wrap gap-2 justify-between items-center">
                        <button
                            onClick={() => setCurrentQuestion(q => Math.max(0, q - 1))}
                            disabled={currentQuestion === 0}
                            className="px-4 py-2 bg-gray-500 text-white rounded disabled:bg-gray-300"
                        >
                            Previous
                        </button>

                        <button
                            onClick={toggleMarkForReview}
                            className={`px-4 py-2 rounded ${
                                markedForReview.has(currentQuestion)
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-purple-100 text-purple-700'
                            }`}
                        >
                            {markedForReview.has(currentQuestion)
                                ? 'Unmark'
                                : 'Mark'}
                        </button>

                        {currentQuestion === mcqs.length - 1 ? (
                            <button
                                onClick={handleSubmitContest}
                                className="px-4 py-2 bg-green-600 text-white rounded"
                            >
                                Submit
                            </button>
                        ) : (
                            <button
                                onClick={() => setCurrentQuestion(q => q + 1)}
                                className="px-4 py-2 bg-blue-500 text-white rounded"
                            >
                                Next
                            </button>
                        )}
                    </div>
                </div>

                {/* ================= MOBILE PALETTE ================= */}
                <div className="sm:hidden mt-6 bg-white border border-black rounded-lg shadow p-4">
                    <h3 className="text-lg font-semibold mb-3">
                        Question Palette
                    </h3>

                    <div className="grid grid-cols-5 gap-3">
                        {mcqs.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentQuestion(index)}
                                className={`h-10 w-10 rounded-md font-semibold border border-black ${getPaletteColor(index)}`}
                            >
                                {index + 1}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ================= DESKTOP PALETTE ================= */}
            <div className="hidden sm:block w-1/4 bg-white border border-black rounded-lg shadow-xl p-4">
                <h3 className="text-xl font-semibold mb-4">Question Palette</h3>

                <div className="grid grid-cols-5 gap-3">
                    {mcqs.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentQuestion(index)}
                            className={`h-10 w-10 rounded-md font-semibold border border-black ${getPaletteColor(index)}`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ProblemCards;
